import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Visibility as ViewIcon } from '@mui/icons-material';
import {
  Box,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  TablePagination,
} from '@mui/material';
import { API_BASE_URL } from '../services/api';

interface StaffPayoutSummary {
  total_earning: number;
  total_paid: number;
  pending_payment: number;
}

interface StaffPayoutItem {
  staff_id: number;
  sp_name: string;
  sp_mobile: string;
  sp_email: string;
  total_earning: number;
  total_paid: number;
  pending_payment: number;
  payout_id?: number;
  payout_amount?: string;
  created_at?: string;
}

interface StaffPayoutResponse {
  pagination: {
    page: number;
    limit: number;
    total_records: number;
    total_pages: number;
    has_more: boolean;
  };
  data: StaffPayoutItem[];
}

const Payouts: React.FC = () => {
  const [summary, setSummary] = useState<StaffPayoutSummary | null>(null);
  const [payouts, setPayouts] = useState<StaffPayoutItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const fetchPayouts = async (currentPage = 0, currentRowsPerPage = rowsPerPage) => {
    try {
      setLoading(true);
      setError(null);

      const url = `${API_BASE_URL}/bookings/getAllStaffPayouts`;
      const params = new URLSearchParams({
        page: String(currentPage + 1),
        limit: String(currentRowsPerPage),
      });

      const response = await fetch(`${url}?${params.toString()}`, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message || 'Failed to load payouts');
      }

      const json = await response.json();
      console.log('API Response:', json); // Debug log
      
      const apiData: StaffPayoutResponse | undefined = json?.data;
      if (!apiData) {
        throw new Error('Invalid response format from server');
      }

      setSummary(null); // Remove summary if not needed
      setPayouts(apiData.data || []);
      setTotalItems(apiData.pagination?.total_records || 0);
    } catch (err: any) {
      console.error('Error fetching all staff payouts:', err);
      setError(err?.message || 'Failed to load payouts');
      setSummary(null);
      setPayouts([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayouts(page, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRows = parseInt(event.target.value, 10);
    setRowsPerPage(newRows);
    setPage(0);
  };

  return (
    <div className="container-xxl flex-grow-1 ">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Typography variant="h4" fontWeight="bold">
          Payouts
        </Typography>
      </div>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        

        {loading && !payouts.length ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Staff Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Mobile</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      Total Earnings
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      Total Paid
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      Pending Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      View
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payouts.length > 0 ? (
                    payouts.map((item) => (
                      <TableRow key={item.staff_id} hover>
                        <TableCell>{item.sp_name}</TableCell>
                        <TableCell>{item.sp_mobile}</TableCell>
                        <TableCell>{item.sp_email}</TableCell>
                        <TableCell>
                          {process.env.REACT_APP_SITE_CURRENCY}
                          {item.total_earning}
                        </TableCell>
                        <TableCell>
                          {process.env.REACT_APP_SITE_CURRENCY}
                          {item.total_paid}
                        </TableCell>
                        <TableCell>
                          {process.env.REACT_APP_SITE_CURRENCY}
                          {item.pending_payment}
                        </TableCell>
                        <TableCell><IconButton
                                                component={Link}
                                                to={`/washers/${item.staff_id}`}
                                                color="primary"
                                                size="small"
                                                title="View Washer Details"
                                                >
                                                <ViewIcon fontSize="small" />
                                              </IconButton></TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No payouts found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalItems}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ mt: 2 }}
            />
          </>
        )}
      </Paper>
    </div>
  );
};

export default Payouts;
